<template>
  <div
    ref="container"
    style="display: flex; flex-direction: column; width: 100%">
    <v-slide-y-transition>
      <div
        v-show="state.open && hasResults"
        ref="popup"
        class="elevation-4 px-3"
        :style="`width: ${dropdownWidth}; left: ${offsetLeft}; top: ${offsetTop};`"
        style="
          position: fixed;
          z-index: 10000;
          height: 470px;
          margin-top: 56px;
          background: var(--dropdown-background);
          border-radius: 2px 2px 14px 14px;
        ">
        <Templates
          v-if="state.mode === 'templates'"
          :excludeUids="[templateUid, state.item.value]"
          :searchQuery="state.searchQuery"
          :tagQuery="state.tagQuery"
          :showTags="false"
          filter="all"
          :showSearchBar="false"
          :height="'100%'"
          :selectMode="true"
          :dense="true"
          return-object
          @selected="selectAssets"
          @results-count="handleResultsCount"
          @loading="handleLoadingState" />
        <Assets
          v-else
          :excludeUids="[templateUid, state.item.value]"
          :searchQuery="state.searchQuery"
          :tagQuery="state.tagQuery"
          :height="'100%'"
          :showTags="false"
          :showSearchBar="false"
          :selectMode="true"
          :dense="true"
          return-object
          @selected="selectAssets"
          @results-count="handleResultsCount"
          @loading="handleLoadingState" />
      </div>
    </v-slide-y-transition>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch, computed } from 'vue';
import { $http } from '@/stores/http';
import { deluge } from '@/stores/deluge.js';
import { state } from '@/stores/deluge.js';

import Assets from './components/Assets/Assets.vue';
import Templates from './components/Templates/Templates.vue';

// Props need to be available early for computed properties
const props = defineProps([
  'item',
  'tagQuery',
  'templateUid',
  'realtime_mode',
  'readOnlyMode',
]);

const loading = ref(false);
const container = ref(null);
const popup = ref(null);
const assetCache = ref('');
const customText = ref(false);
const currentScrollY = ref(0);
const currentScrollX = ref(0);
const forceUpdate = ref(0); // Used to force computed properties to recalculate
const resultsCount = ref(1); // Track actual filtered results count from child component (start at 1 to show while loading)
const isSearching = ref(false); // Track if Assets/Templates are currently loading

// Validate that textArea still exists in the DOM
function isTextAreaValid() {
  if (!state.textArea?.$el) return false;

  try {
    // Check if element is still connected to the DOM
    if (!state.textArea.$el.isConnected) return false;

    // Check if we can get bounding rect (element is rendered)
    const rect = state.textArea.$el.getBoundingClientRect?.();
    return rect !== undefined;
  } catch (e) {
    return false;
  }
}

const dropdownWidth = computed(() => {
  // Include forceUpdate as a dependency to trigger recalculation
  const _ = forceUpdate.value;

  if (!isTextAreaValid()) {
    // Close overlay if textArea is invalid
    if (state.open) {
      state.open = false;
    }
    return '0px'; // Use 0px instead of 100% to avoid large overlay
  }

  try {
    const width = state.textArea.$el.offsetWidth;
    return width ? width + 'px' : '300px'; // Fallback to reasonable default
  } catch (e) {
    state.open = false;
    return '0px';
  }
});

const offsetTop = computed(() => {
  // Include forceUpdate as a dependency to trigger recalculation
  const _ = forceUpdate.value;

  if (!isTextAreaValid()) {
    if (state.open) {
      state.open = false;
    }
    return '-9999px'; // Move offscreen if invalid
  }

  try {
    const rect = state.textArea.$el.getBoundingClientRect();
    const top = rect.top + currentScrollY.value;
    return top + 'px';
  } catch (e) {
    state.open = false;
    return '-9999px';
  }
});

const offsetLeft = computed(() => {
  // Include forceUpdate as a dependency to trigger recalculation
  const _ = forceUpdate.value;

  if (!isTextAreaValid()) {
    if (state.open) {
      state.open = false;
    }
    return '-9999px'; // Move offscreen if invalid
  }

  try {
    const rect = state.textArea.$el.getBoundingClientRect();
    const left = rect.left + currentScrollX.value;
    return left + 'px';
  } catch (e) {
    state.open = false;
    return '-9999px';
  }
});

// Determine if there are any matching results based on child component's actual filtered count
const hasResults = computed(() => {
  // Don't show dropdown while searching/loading
  if (isSearching.value) {
    return false;
  }

  // Always show dropdown initially or when no search query (allow browsing)
  if (!state.searchQuery || state.searchQuery.trim() === '') return true;

  // Only show when we have actual results (not 0 and not loading)
  return resultsCount.value > 0;
});

const handleScroll = () => {
  if (state.open && !isTextAreaValid()) {
    state.open = false;
    return;
  }

  currentScrollY.value = window.scrollY;
  currentScrollX.value = window.scrollX;
};

const handleResize = () => {
  if (state.open && !isTextAreaValid()) {
    state.open = false;
    return;
  }

  if (state.open) {
    // Force recomputation by incrementing forceUpdate
    forceUpdate.value++;
    currentScrollY.value = window.scrollY;
    currentScrollX.value = window.scrollX;
  }
};

// Watch for textArea validity when overlay is open
let validationInterval = null;
watch(
  () => state.open,
  (isOpen, wasOpen) => {
    if (isOpen) {
      // Don't reset resultsCount - let loading state control visibility
      if (!wasOpen) {
        // Opening from closed state
      }

      // Validate textArea existence periodically while open
      validationInterval = setInterval(() => {
        if (!isTextAreaValid()) {
          state.open = false;
        }
      }, 100); // Check every 100ms
    } else {
      // Clear validation when closed
      if (validationInterval) {
        clearInterval(validationInterval);
        validationInterval = null;
      }
    }
  }
);

function handleResultsCount(count) {
  resultsCount.value = count;
}

function handleLoadingState(loading) {
  isSearching.value = loading;
  state.isSearching = loading; // Pass to state for textbox to use
}

// Reset resultsCount when search starts so dropdown stays visible during API call
watch(
  () => state.searchQuery,
  (newVal, oldVal) => {
    // Don't reset resultsCount here - let the loading state control visibility
    if (customText.value) {
      state.item.value = state.searchQuery;
    }
  }
);

function selectAssets(assets) {
  customText.value = false;

  state.item.value = assets[0].uid;
  state.searchQuery = assets[0].display_name || assets[0].name;
  assetCache.value = assets[0];
  state.itemAdded = { uid: assets[0].uid, item: assets[0] };
}

function handleClickOutside(event) {
  // Validate textArea before checking click position
  if (!isTextAreaValid()) {
    state.open = false;
    return;
  }

  // Check if click is outside both popup and the textArea it's attached to
  const clickedInPopup = popup.value && popup.value.contains(event.target);
  const clickedInTextArea =
    state.textArea?.$el && state.textArea.$el.contains(event.target);

  if (!clickedInPopup && !clickedInTextArea) {
    state.open = false;
  }
}

onMounted(() => {
  // Delay slightly to let Vue fully mount the component
  setTimeout(() => {
    document.addEventListener('click', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true); // Use capture phase
    window.addEventListener('resize', handleResize);
  }, 250);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside);
  window.removeEventListener('scroll', handleScroll, true);
  window.removeEventListener('resize', handleResize);

  // Clear validation interval
  if (validationInterval) {
    clearInterval(validationInterval);
    validationInterval = null;
  }
});
</script>
<style scoped></style>
